/**
 * Folder tree for the Documents page: "All documents" and "Not yet filed" always on top, then the folders.
 * Top-level folders come with /api/fd/home; children load when a folder opens.
 * Selection: null = all documents, "" = not yet filed, otherwise a filing path.
 */
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronRight, Folder, FolderOpen, Inbox, Library, Loader2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { api } from "../../lib/api";
import { FK, folderUrl, type FdFolder, type FdFolders } from "./fd";

export function FolderTree({ folders, unfiled, total, selected, onSelect }: {
  folders: FdFolder[]; unfiled?: number; total?: number | null; selected: string | null; onSelect: (path: string | null) => void;
}) {
  return (
    <nav aria-label="Folders" className="text-sm">
      <Pinned icon={<Library size={15} aria-hidden />} label="All documents" count={total} active={selected === null} onClick={() => onSelect(null)} />
      <Pinned icon={<Inbox size={15} aria-hidden />} label="Not yet filed" count={unfiled} active={selected === ""} onClick={() => onSelect("")} />
      {folders.length > 0 ? (
        <ul role="tree" className="mt-1 border-t border-[var(--line)] pt-1">
          {folders.map((f) => <Node key={f.path} folder={f} depth={0} selected={selected} onSelect={onSelect} />)}
        </ul>
      ) : (
        <p className="muted mt-1 border-t border-[var(--line)] px-2 pb-1 pt-2 text-xs">No folders yet. Open a document and use Move to file it.</p>
      )}
    </nav>
  );
}

function Pinned({ icon, label, count, active, onClick }: { icon: ReactNode; label: string; count?: number | null; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "true" : undefined}
      className={clsx("flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left font-medium", active ? "bg-brand-50 text-brand-700" : "hover:bg-[var(--panel-2)]")}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count != null && <span className="muted shrink-0 text-xs num">{count.toLocaleString("en-GB")}</span>}
    </button>
  );
}

function Node({ folder, depth, selected, onSelect }: { folder: FdFolder; depth: number; selected: string | null; onSelect: (path: string | null) => void }) {
  const inside = !!selected && selected.startsWith(`${folder.path}/`);
  const [open, setOpen] = useState(inside);
  const kids = useQuery({
    queryKey: FK.folders(folder.path),
    queryFn: () => api<FdFolders>(folderUrl(folder.path)),
    enabled: open && folder.hasChildren !== false,
    staleTime: 5 * 60_000,
  });
  const active = selected === folder.path;
  const canOpen = folder.hasChildren !== false;
  return (
    <li role="treeitem" aria-expanded={canOpen ? open : undefined} aria-selected={active}>
      <div className={clsx("flex items-center rounded-lg", active ? "bg-brand-50 text-brand-700" : "hover:bg-[var(--panel-2)]")} style={{ paddingLeft: depth * 12 }}>
        <button
          type="button"
          className={clsx("grid h-7 w-6 shrink-0 place-items-center", !canOpen && "invisible")}
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? `Close ${folder.name}` : `Open ${folder.name}`}
          tabIndex={canOpen ? 0 : -1}
        >
          <ChevronRight size={14} className={clsx("muted transition-transform", open && "rotate-90")} />
        </button>
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2 py-1.5 pr-2 text-left" onClick={() => { onSelect(folder.path); if (canOpen) setOpen(true); }}>
          {active || open ? <FolderOpen size={15} className="shrink-0 text-brand-600" aria-hidden /> : <Folder size={15} className="muted shrink-0" aria-hidden />}
          <span className="min-w-0 flex-1 truncate">{folder.name}</span>
          {folder.count != null && <span className="muted shrink-0 text-xs num">{folder.count}</span>}
        </button>
      </div>
      {open && canOpen && (
        kids.isLoading
          ? <p className="muted flex items-center gap-1.5 py-1 text-xs" style={{ paddingLeft: depth * 12 + 30 }}><Loader2 size={12} className="animate-spin" /> Loading…</p>
          : (kids.data?.items.length ?? 0) > 0 && (
            <ul role="group">
              {kids.data!.items.map((k) => <Node key={k.path} folder={k} depth={depth + 1} selected={selected} onSelect={onSelect} />)}
            </ul>
          )
      )}
    </li>
  );
}
